import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PermissionService } from '../../../../core/services/permission.service';
import { GroupBrandService } from '../../../../core/services/group-brand.service';
import { distinctUntilChanged, interval, skip } from 'rxjs';
import { BaseComponent } from '../../../../core/components/base/base.component';
import { KEYCLOAK_ROLES } from '../../../../shared/constants/auth.constants';
import {
  DateRange,
  DateRangePickerComponent,
} from '../../../../shared/components/date-range-picker/date-range-picker.component';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { DashboardActionsComponent } from '../../components/dashboard-actions/dashboard-actions.component';
import { DashboardConferenceComponent } from '../../components/dashboard-conference/dashboard-conference.component';
import { DashboardFirstStepsComponent } from '../../components/dashboard-first-steps/dashboard-first-steps.component';
import { DashboardConnectionsComponent } from '../../components/dashboard-connections/dashboard-connections.component';
import { DashboardChannelFilterComponent } from '../../components/dashboard-channel-filter/dashboard-channel-filter.component';
import { DashboardKpiCardsComponent } from '../../components/dashboard-kpi-cards/dashboard-kpi-cards.component';
import { DashboardRevenueChartComponent } from '../../components/dashboard-revenue-chart/dashboard-revenue-chart.component';
import { DashboardChannelsComponent } from '../../components/dashboard-channels/dashboard-channels.component';
import { DashboardInvoiceFlowComponent } from '../../components/dashboard-invoice-flow/dashboard-invoice-flow.component';
import { DashboardOrderFlowComponent } from '../../components/dashboard-order-flow/dashboard-order-flow.component';
import { DashboardResultComponent } from '../../components/dashboard-result/dashboard-result.component';
import { FinancialReconciliationService } from '../../../financial-reconciliation/services/financial-reconciliation.service';
import { FinancialReconciliationSummary } from '../../../financial-reconciliation/models/financial-reconciliation.model';
import { ActivityService } from '../../../../core/realtime/activity.service';
import { LocalStorageService } from '../../../../shared/services/local-storage.service';
import { blockerRecords, pendingCriticalTopics, pendingTopics } from '../../../pendings/models/pending-kinds';
import { PendingsService } from '../../../pendings/services/pendings.service';
import { SaleConferencesService } from '../../../sale-conferences/services/sale-conferences.service';
import { TopChecker } from '../../../sale-conferences/models/sale-conference.model';
import { DashboardKpis, DashboardSnapshot } from '../../models/dashboard.model';
import { DashboardService } from '../../services/dashboard.service';
import { ActionPendings } from '../../utils/dashboard-actions';
import { readChannel, saveChannel } from '../../utils/dashboard-channel';
import { shortPeriodStart } from '../../utils/dashboard-first-activity';
import { timeAgo } from '../../utils/dashboard-format';
import { nextFreshness } from '../../utils/dashboard-freshness';
import { hasNoOrders } from '../../utils/dashboard-first-steps';
import { kpisFromSnapshot } from '../../utils/dashboard-kpis';
import { restoreDashboardPeriod } from '../../utils/dashboard-period';
import {
  DashboardSectionId,
  restoreDashboardSections,
  toggleDashboardSection,
} from '../../utils/dashboard-sections';

// Container do painel: busca o snapshot, controla período/refresh/troca de empresa e distribui os
// dados aos widgets presentacionais (cada card é um componente com :host{display:contents} para
// preservar a distribuição das linhas .row.dash-row). Toda a derivação/gráficos vive nos widgets.
@Component({
  selector: 'app-home-index',
  templateUrl: './home-index.component.html',
  styleUrl: './home-index.component.scss',
  standalone: true,
  imports: [
    NgbTooltipModule,
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    HasPermissionDirective,
    DateRangePickerComponent,
    DashboardActionsComponent,
    DashboardChannelFilterComponent,
    DashboardKpiCardsComponent,
    DashboardRevenueChartComponent,
    DashboardChannelsComponent,
    DashboardOrderFlowComponent,
    DashboardInvoiceFlowComponent,
    DashboardConnectionsComponent,
    DashboardConferenceComponent,
    DashboardFirstStepsComponent,
    DashboardResultComponent,
  ],
})
export class HomeIndexComponent extends BaseComponent implements OnInit {
  public nomeUsuario = '';
  // Empresa ativa do jeito que o seletor do menu mostra: apelido e grupo. A razão social fica
  // só no tooltip.
  public tenantName = '';
  public tenantGroup = '';
  public tenantLegalName = '';
  public roles = KEYCLOAK_ROLES;

  public snapshot?: DashboardSnapshot;
  // Resultado do período (resumo da conciliação: vendas com nota). Só pra quem vê pedidos.
  public result?: FinancialReconciliationSummary;
  private readonly reconciliation = inject(FinancialReconciliationService);
  private readonly pendingsService = inject(PendingsService);
  private readonly conferences = inject(SaleConferencesService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  // Canal escolhido no filtro (integrationId); null = "Todos os canais". Só vale depois de
  // conferido contra os canais da empresa ativa (channelsKnown).
  public channelId: number | null = null;
  // Fila de pendências da empresa (api/pendings, sem filtro de canal), resumida pro Pede ação agora.
  public pendings: ActionPendings | null = null;
  // `?canal=` pedido pela URL e ainda não conferido. A troca de empresa descarta.
  private urlChannel: string | null = null;
  // Os canais da empresa ativa já chegaram num snapshot. Antes disso nenhum integrationId sai pra
  // API: um id guardado de outra empresa (ou removido) daria 400/403, então a primeira carga vai
  // sem filtro, confere o id e só então refiltra.
  private channelsKnown = false;
  // Só a última carga vale (canal/período/empresa trocados rápido).
  private loadSeq = 0;
  private pendingsSeq = 0;
  private rankingSeq = 0;
  // Ranking de conferentes do período (todos, não só o pódio do snapshot): o total da linha da
  // Conferência soma todos os conferentes. undefined = carregando; null = falhou (cai no pódio do snapshot).
  private ranking?: TopChecker[] | null;
  // Identidade do grupo (reativa): a capa do topo só existe com cor.
  public readonly groupBrand = inject(GroupBrandService);
  // URL do logo que falhou ao carregar: guardada por URL, então trocar de empresa/logo tenta de novo.
  public failedLogoUrl: string | null = null;
  public filters: FormGroup;

  // Sem data mínima: pedidos, notas e pagamentos não expiram (só os logs de processamento têm
  // retenção). Dá pra olhar um ano inteiro e comparar com outro filtrando um de cada vez.
  public readonly periodMaxDate = this.daysAgo(0);

  // Roles que gateiam ao menos UM card do painel. Se o usuário não tem NENHUMA, o painel
  // renderiza vazio — mostramos um empty-state em vez de um vão em branco (consistência de UX
  // independente das roles). Manter em sincronia com os *appHasPermission dos widgets.
  private static readonly DASHBOARD_ROLES: string[] = [
    KEYCLOAK_ROLES.INVOICE,
    KEYCLOAK_ROLES.ORDER,
    KEYCLOAK_ROLES.INTEGRATION,
    KEYCLOAK_ROLES.ADVERTISEMENT,
    // A conferência é o resumo do ranking: quem só confere não vê o total.
    KEYCLOAK_ROLES.SALE_CONFERENCE_RANKING,
  ];

  private static readonly PERIOD_KEY = 'mc-dash-period';
  private static readonly SECTIONS_KEY = 'mc-dash-sections';

  // Seções colapsáveis do painel (home enxuta). O essencial (alertas, KPIs, erros de ERP,
  // administração) fica SEMPRE visível; o resto abre sob demanda pelos botões do topo.
  // roles = any-of, espelhando os *appHasPermission dos widgets da seção — sem nenhuma delas
  // a seção renderizaria vazia, então o botão some junto.
  public readonly sectionDefs: {
    id: DashboardSectionId;
    label: string;
    icon: string;
    roles: string[];
  }[] = [
    {
      id: 'vendas',
      label: 'Vendas',
      icon: 'ti-chart-bar',
      roles: [KEYCLOAK_ROLES.INVOICE, KEYCLOAK_ROLES.ORDER],
    },
    { id: 'financeiro', label: 'Financeiro', icon: 'ti-wallet', roles: [KEYCLOAK_ROLES.ORDER] },
    {
      id: 'integracoes',
      label: 'Integrações',
      icon: 'ti-plug-connected',
      roles: [KEYCLOAK_ROLES.INTEGRATION],
    },
    {
      id: 'conferencia',
      label: 'Conferência',
      icon: 'ti-users',
      roles: [KEYCLOAK_ROLES.SALE_CONFERENCE_RANKING],
    },
  ];

  // Seções abertas — restaurada do storage no ngOnInit (default: só Vendas).
  public openSections: DashboardSectionId[] = [];

  // Momento do último snapshot carregado — vira o "Atualizado há X" do cabeçalho.
  public lastUpdated?: Date;
  // Tick de 1 min só pra revalidar o label relativo (o getter lê o campo pra disparar CD).
  private clockTick = 0;

  // Eventos do feed ao vivo chegados DEPOIS do último load → botão "Novos dados".
  public hasFreshData = false;
  private seenEventsCount = 0;
  private readonly activity = inject(ActivityService);

  // Permissão efetiva (teto do Keycloak cruzado com o perfil de acesso), não as roles do token.
  private readonly permissions = inject(PermissionService);

  constructor(
    private fb: FormBuilder,
    private localStorageService: LocalStorageService,
    private dashboardService: DashboardService,
  ) {
    super();
    this.filters = this.fb.group({ period: [this.defaultPeriod()] });

    // Evento novo no feed (SignalR) depois do último load = números da tela possivelmente
    // defasados → oferece refresh em vez de recarregar sozinho (não puxar o tapete do usuário).
    // O botão "Limpar" do feed (navbar) pode encolher events().length a qualquer momento — sem
    // reancorar pra baixo aqui, um clique em "Limpar" zerava o feed e o aviso ficava mudo até
    // acumular 6+ eventos de novo (regra de re-ancoragem pura e testada em nextFreshness).
    effect(() => {
      const mark = nextFreshness(
        { seen: this.seenEventsCount, fresh: this.hasFreshData },
        this.activity.events().length,
      );
      this.seenEventsCount = mark.seen;
      this.hasFreshData = mark.fresh;
    });
  }

  // True se alguma seção do painel é visível pro perfil atual. Falso → empty-state.
  public get hasAnyDashboardWidget(): boolean {
    return this.permissions.hasAny(HomeIndexComponent.DASHBOARD_ROLES);
  }

  ngOnInit(): void {
    const session = this.localStorageService.getSession();
    this.nomeUsuario = session?.name ?? '';
    const tenant = session?.tenant;
    this.tenantName = tenant?.fantasyName || tenant?.name || '';
    this.tenantGroup = tenant?.companyGroupName ?? '';
    // Tooltip só quando acrescenta algo (apelido diferente da razão social).
    this.tenantLegalName = tenant?.name && tenant.name !== this.tenantName ? tenant.name : '';

    this.openSections = restoreDashboardSections(
      localStorage.getItem(HomeIndexComponent.SECTIONS_KEY),
    );

    this.urlChannel = this.route.snapshot.queryParamMap.get('canal');
    this.load();

    this.subscriptions.add(
      // Só a troca de empresa: a identidade do grupo reemite o mesmo tenant (GroupBrandService.refresh).
      this.observableContext()
        .pipe(distinctUntilChanged(), skip(1))
        .subscribe(() => {
          // Troca de empresa: números da empresa anterior não podem ficar na tela nem
          // esmaecidos — derruba o snapshot pra voltar ao skeleton.
          this.snapshot = undefined;
          this.result = undefined;
          this.pendings = null;
          this.ranking = undefined;
          // O canal é da empresa: o `?canal=` da URL e o escolhido valiam pra anterior. O salvo da
          // empresa nova (chave por empresa) é conferido contra os canais dela quando chegarem.
          this.channelId = null;
          this.channelsKnown = false;
          this.urlChannel = null;
          this.syncChannelUrl();
          this.load();
        }),
    );

    // Mudou o período → recarrega na hora (antes exigia clicar em "Atualizar") e persiste a
    // escolha pra próxima visita.
    this.subscriptions.add(
      this.filters.get('period')!.valueChanges.subscribe(() => {
        this.persistPeriod();
        this.load();
      }),
    );

    // Revalida o "Atualizado há X min" a cada minuto (label relativo não muda sozinho).
    this.subscriptions.add(interval(60_000).subscribe(() => this.clockTick++));
  }

  // Skeleton só no PRIMEIRO load (nada na tela ainda). Recargas (período/refresh/troca de
  // empresa) mantêm o conteúdo visível com um dim leve — o usuário nunca perde a página.
  public get showSkeleton(): boolean {
    return this.isLoading && !this.snapshot;
  }

  public get refreshing(): boolean {
    return this.isLoading && !!this.snapshot;
  }

  public load(): void {
    this.isLoading = true;
    this.periodParams = this.computePeriodParams();
    const { from, to } = this.periodRange();
    const seq = ++this.loadSeq;

    this.loadResult(from, to, seq);
    this.loadPendings();
    this.loadRanking(from, to);
    this.fetchSnapshot(from, to, seq);
  }

  private fetchSnapshot(from: string | undefined, to: string | undefined, seq: number): void {
    // Canal desconhecido ainda: a primeira carga da empresa vai sem integrationId.
    const requested = this.channelsKnown ? this.channelId : null;

    this.dashboardService.snapshot(from, to, requested ?? undefined).subscribe({
      next: (data) => {
        if (seq !== this.loadSeq) return;

        const channels = data.channels ?? [];
        const companyId = this.stateService.contextAtual ?? 0;
        let stale = false;
        let syncUrl = false;

        if (!this.channelsKnown) {
          // Conferência única por empresa: URL (`?canal=`) ou o salvo desta empresa, só se o id é de
          // um canal dela. Qualquer outra coisa volta pra "Todos os canais".
          this.channelsKnown = true;
          this.channelId = readChannel(companyId, this.urlChannel, channels);
          // URL pediu um canal (inválido volta pra Todos e sai da URL) ou o salvo passa a valer (entra na URL).
          syncUrl = this.urlChannel !== null || this.channelId !== null;
          this.urlChannel = null;
          stale = this.channelId !== null;
        } else if (this.channelId !== null && channels.length && !channels.some((c) => c.integrationId === this.channelId)) {
          // O canal escolhido deixou de existir na empresa.
          this.channelId = null;
          saveChannel(companyId, null);
          syncUrl = true;
          stale = true;
        }

        if (syncUrl) this.syncChannelUrl();

        if (stale) {
          // O que veio não é do canal escolhido: refiltra (snapshot e conciliação) sem mostrar o intermediário.
          const next = ++this.loadSeq;
          this.loadResult(from, to, next);
          this.fetchSnapshot(from, to, next);
          return;
        }

        this.snapshot = data;
        this.lastUpdated = new Date();
        this.hasFreshData = false;
        this.seenEventsCount = this.activity.events().length;
        this.isLoading = false;
      },
      error: () => {
        if (seq === this.loadSeq) this.isLoading = false;
      },
    });
  }

  // Pedido separado do snapshot: é a regra da conciliação (vendas com nota, impostos do XML, custo
  // congelado) — mesmo número que a tela da conciliação mostra pro mesmo período. Filtra pelo canal
  // escolhido; antes de conferir os canais da empresa vai sem filtro.
  private loadResult(from: string | undefined, to: string | undefined, seq: number): void {
    if (!this.permissions.has(KEYCLOAK_ROLES.ORDER)) return;
    const channel = this.channelsKnown ? this.channelId : null;
    this.reconciliation
      .getSummary({ startDate: from, endDate: to, integrationIds: channel !== null ? [channel] : undefined })
      .subscribe({
        next: (summary) => {
          if (seq === this.loadSeq) this.result = summary;
        },
        error: () => {
          if (seq === this.loadSeq) this.result = undefined;
        },
      });
  }

  // O snapshot traz só os 3 primeiros conferentes (pódio): a linha "N conferências por N conferentes"
  // precisa de todos, então pede o ranking do período (teto da API: 50). Sem a permissão não pede.
  private loadRanking(from: string | undefined, to: string | undefined): void {
    if (!this.permissions.has(KEYCLOAK_ROLES.SALE_CONFERENCE_RANKING)) return;
    const seq = ++this.rankingSeq;
    this.conferences.topCheckers(from, to, 50).subscribe({
      next: (rows) => {
        if (seq === this.rankingSeq) this.ranking = rows;
      },
      error: () => {
        if (seq === this.rankingSeq) this.ranking = null;
      },
    });
  }

  // Conferentes da linha da Conferência: undefined enquanto carrega.
  public get conferenceCheckers(): { total: number; divergent: number }[] | undefined {
    if (this.ranking === undefined) return undefined;
    return this.ranking ?? (this.snapshot ? (this.snapshot.topCheckers ?? []) : undefined);
  }

  // Fila de pendências (estado atual, sem período nem canal) pro item "Pendências" do Pede ação agora.
  private loadPendings(): void {
    if (
      !this.permissions.hasAny([
        KEYCLOAK_ROLES.ORDER,
        KEYCLOAK_ROLES.INVOICE,
        KEYCLOAK_ROLES.ADVERTISEMENT,
        KEYCLOAK_ROLES.INTEGRATION,
      ])
    ) {
      return;
    }
    // Falha silenciosa: o item é complemento e o erro da fila pertence à tela dela.
    const seq = ++this.pendingsSeq;
    this.pendingsService.list().subscribe({
      next: (queue) => {
        if (seq !== this.pendingsSeq) return;
        const items = queue.items ?? [];
        const blockers = queue.blockers ?? [];
        this.pendings = {
          topics: pendingTopics({ items, blockers }),
          critical: pendingCriticalTopics({ items, blockers }),
          blocked: blockers.reduce((sum, b) => sum + blockerRecords(b), 0),
        };
      },
      error: () => {
        if (seq === this.pendingsSeq) this.pendings = null;
      },
    });
  }

  // ---------- filtro de canal ----------
  public onChannelChange(id: number | null): void {
    this.channelId = id;
    const companyId = this.stateService.contextAtual;
    if (companyId) saveChannel(companyId, id);
    this.syncChannelUrl();
    this.load();
  }

  // `?canal=<integrationId>` na URL (sem empilhar histórico); null remove o parâmetro.
  private syncChannelUrl(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { canal: this.channelId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // Indicadores do período: `kpis` da API nova ou, sem ele, o mapeamento dos campos antigos do
  // snapshot (ver kpisFromSnapshot). Memoizado pelo snapshot para a referência ficar estável.
  private kpisCache?: { source: DashboardSnapshot; kpis: DashboardKpis | undefined };
  public get kpis(): DashboardKpis | undefined {
    const snap = this.snapshot;
    if (!snap) return undefined;
    if (this.kpisCache?.source !== snap) this.kpisCache = { source: snap, kpis: kpisFromSnapshot(snap) };
    return this.kpisCache.kpis;
  }

  // Aviso de período curto: a primeira atividade (da empresa, ou do canal escolhido) cai dentro do
  // período. Devolve a data (dd/MM/yyyy) ou null.
  public get shortPeriodDate(): string | null {
    const snap = this.snapshot;
    if (!snap) return null;
    const first =
      this.channelId !== null
        ? snap.channels?.find((c) => c.integrationId === this.channelId)?.firstActivityAt
        : snap.firstActivityAt;
    return shortPeriodStart(first, this.periodParams);
  }

  // Empresa sem nenhum pedido: primeiros passos no lugar dos widgets. Perfil sem nenhum indicador
  // fica com o empty-state. Sem `firstActivityAt` (API antiga) a home segue normal.
  public get showFirstSteps(): boolean {
    return this.hasAnyDashboardWidget && hasNoOrders(this.snapshot);
  }

  public get lastUpdatedLabel(): string {
    if (!this.lastUpdated) return '';
    void this.clockTick; // lido só pra CD reavaliar o label a cada tick
    return timeAgo(this.lastUpdated.toISOString());
  }

  // from/to yyyy-MM-dd — deep-links dos widgets abrem as listagens no MESMO recorte do painel.
  // CAMPO (não getter): precisa de referência ESTÁVEL entre change detections — objeto novo a
  // cada CD dispara o ngOnChanges dos widgets de gráfico em loop (rebuild infinito do Apex,
  // trava o browser). Atualizado junto do load().
  public periodParams?: { from: string; to: string };

  private computePeriodParams(): { from: string; to: string } | undefined {
    const period: DateRange | null = this.filters.get('period')?.value;
    return period?.startDate && period?.endDate
      ? { from: period.startDate, to: period.endDate }
      : undefined;
  }

  // ---------- seções colapsáveis ----------
  public sectionOn(id: DashboardSectionId): boolean {
    return this.openSections.includes(id);
  }

  public toggleSection(id: DashboardSectionId): void {
    this.openSections = toggleDashboardSection(this.openSections, id);
    localStorage.setItem(HomeIndexComponent.SECTIONS_KEY, JSON.stringify(this.openSections));
  }

  // Botões só das seções que o perfil consegue ver (any-of, igual aos widgets).
  public get visibleSectionDefs(): typeof this.sectionDefs {
    return this.sectionDefs.filter((s) => this.permissions.hasAny(s.roles));
  }

  // Empty state dos widgets: amplia pros últimos 90 dias e recarrega.
  public widenPeriod(): void {
    this.filters.get('period')?.setValue({
      startDate: this.toIsoDay(this.daysAgo(90)),
      endDate: this.toIsoDay(new Date()),
    });
  }

  // ---------- período ----------
  private periodRange(): { from?: string; to?: string } {
    const period: DateRange | null = this.filters.get('period')?.value;
    return {
      from: period?.startDate ? new Date(`${period.startDate}T00:00:00`).toISOString() : undefined,
      to: period?.endDate ? new Date(`${period.endDate}T23:59:59.999`).toISOString() : undefined,
    };
  }
  private defaultPeriod(): DateRange {
    const stored = this.restorePeriod();
    if (stored) return stored;

    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 30);
    return { startDate: this.toIsoDay(from), endDate: this.toIsoDay(to) };
  }

  private persistPeriod(): void {
    const period: DateRange | null = this.filters.get('period')?.value;
    if (!period?.startDate || !period?.endDate) {
      localStorage.removeItem(HomeIndexComponent.PERIOD_KEY);
      return;
    }
    localStorage.setItem(
      HomeIndexComponent.PERIOD_KEY,
      JSON.stringify({ ...period, savedOn: this.toIsoDay(new Date()) }),
    );
  }

  // Restaura o último período escolhido (regra de re-ancoragem vive em restoreDashboardPeriod,
  // pura e testada). Storage inválido cai no default de 30 dias.
  private restorePeriod(): DateRange | null {
    return restoreDashboardPeriod(localStorage.getItem(HomeIndexComponent.PERIOD_KEY), new Date());
  }
  private daysAgo(days: number): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - days);
    return d;
  }
  private toIsoDay(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
}
